/**
 * Ingredients List Page
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Package, AlertTriangle, CheckCircle } from 'lucide-react';
import { inventoryApi } from '../lib/api/inventory';
import type { Ingredient } from '../types/inventory';
import { IngredientCategory, CATEGORY_LABELS } from '../types/inventory';
import { IngredientCard } from '../components/inventory/IngredientCard';

export default function IngredientsListPage() {
  const [loading, setLoading] = useState(true);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [filteredIngredients, setFilteredIngredients] = useState<Ingredient[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<IngredientCategory | 'ALL'>('ALL');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  useEffect(() => {
    loadIngredients();
  }, [showActiveOnly]);

  useEffect(() => {
    applyFilters();
  }, [ingredients, searchQuery, categoryFilter, showLowStockOnly]);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const data = await inventoryApi.getIngredients(showActiveOnly);
      setIngredients(data);
    } catch (error) {
      console.error('Failed to load ingredients:', error);
      alert('Errore nel caricamento degli ingredienti');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...ingredients];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ing) =>
          ing.name.toLowerCase().includes(query) ||
          ing.description?.toLowerCase().includes(query) ||
          ing.supplierName?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter((ing) => ing.category === categoryFilter);
    }

    // Low stock filter
    if (showLowStockOnly) {
      filtered = filtered.filter((ing) => ing.isLowStock || ing.isCriticalStock);
    }

    setFilteredIngredients(filtered);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mb-3"></div>
          <p className="text-gray-500 dark:text-white/40">Caricamento Ingredienti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Ingredienti</h1>
            <p className="text-gray-500 dark:text-white/40 text-sm mt-1">Gestisci tutti gli ingredienti del magazzino</p>
          </div>
          <Link
            to="/dashboard/inventory/ingredients/new"
            className="px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center gap-2 font-medium"
          >
            <Plus size={18} />
            Nuovo Ingrediente
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-4 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per nome, descrizione o fornitore..."
              className="w-full px-4 py-2.5 pl-10 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition"
            />
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-white/30" />
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as IngredientCategory | 'ALL')}
              className="px-4 py-2 bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500/50 focus:border-transparent transition text-sm"
            >
              <option value="ALL">Tutte le categorie</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            {/* Low Stock Toggle */}
            <button
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className={`px-4 py-2 rounded-xl font-medium text-sm transition-all flex items-center gap-2 ${
                showLowStockOnly
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-50 dark:bg-white/[0.04] text-gray-700 dark:text-white/70 border border-gray-200 dark:border-white/[0.08] hover:bg-gray-100 dark:hover:bg-white/[0.06]'
              }`}
            >
              <AlertTriangle size={16} />
              {showLowStockOnly ? 'Scorte basse' : 'Tutte le scorte'}
            </button>

            {/* Active Only Toggle */}
            <button
              onClick={() => setShowActiveOnly(!showActiveOnly)}
              className={`px-4 py-2 rounded-xl font-medium text-sm transition-all flex items-center gap-2 ${
                showActiveOnly
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-50 dark:bg-white/[0.04] text-gray-700 dark:text-white/70 border border-gray-200 dark:border-white/[0.08] hover:bg-gray-100 dark:hover:bg-white/[0.06]'
              }`}
            >
              <CheckCircle size={16} />
              {showActiveOnly ? 'Solo attivi' : 'Tutti'}
            </button>

            {/* Results Count */}
            <div className="ml-auto text-sm text-gray-500 dark:text-white/40">
              {filteredIngredients.length} ingredienti
            </div>
          </div>
        </div>
      </div>

      {/* Ingredients Grid */}
      {filteredIngredients.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-gray-400 dark:text-white/30" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Nessun ingrediente trovato</h3>
          <p className="text-sm text-gray-500 dark:text-white/40">
            Prova a modificare i filtri o aggiungi un nuovo ingrediente
          </p>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filteredIngredients.map((ingredient, index) => (
            <motion.div
              key={ingredient.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <IngredientCard
                ingredient={ingredient}
                onClick={() => alert(`Dettagli: ${ingredient.name} (Coming soon!)`)}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
